/** Sidebar в стиле Saleor Dashboard.
 *
 * Структура:
 *  - Top-level link (Дашборд)
 *  - Группы (collapsible) — Каталог / Заказы / Клиенты / Маркетинг / Контент
 *  - Аналитика (top-level)
 *  - Bottom block — Магазины / Конфигурация / Настройки / Юзеры
 *
 * Поведение групп:
 *  - Авто-раскрытие, если активна одна из child-страниц
 *  - Manual toggle, состояние сохраняется в localStorage
 */

import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BarChart3,
  Boxes,
  ChevronDown,
  FileText,
  FolderTree,
  Gift,
  Home,
  Image as ImageIcon,
  Layers,
  Megaphone,
  Package,
  Pencil,
  Settings,
  ShoppingCart,
  SlidersHorizontal,
  Star,
  Store,
  Tag,
  Ticket,
  Upload,
  UserCog,
  Users,
} from "lucide-react";

import { useAuthStore } from "@/stores/auth";
import { canAny } from "@/lib/permissions";
import type { UserRole } from "@/lib/types";
import { cn } from "@/lib/utils";

interface Leaf {
  type: "leaf";
  to: string;
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
  permissions?: string[];
}

interface Group {
  type: "group";
  key: string;
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
  items: Leaf[];
}

type NavEntry = Leaf | Group;

const TOP: NavEntry[] = [
  {
    type: "leaf",
    to: "/",
    labelKey: "nav.dashboard",
    icon: Home,
  },
  {
    type: "group",
    key: "catalog",
    labelKey: "nav.catalog",
    icon: Package,
    items: [
      {
        type: "leaf",
        to: "/products",
        labelKey: "nav.products",
        icon: Package,
        permissions: ["products:list"],
      },
      {
        type: "leaf",
        to: "/categories",
        labelKey: "nav.categories",
        icon: FolderTree,
        permissions: ["categories:list"],
      },
      {
        type: "leaf",
        to: "/stock",
        labelKey: "nav.stock",
        icon: Boxes,
        permissions: ["stock:list"],
      },
      {
        type: "leaf",
        to: "/configuration/attributes",
        labelKey: "nav.attributes",
        icon: Layers,
        permissions: ["products:list"],
      },
      {
        type: "leaf",
        to: "/configuration/product-types",
        labelKey: "nav.productTypes",
        icon: Layers,
        permissions: ["products:list"],
      },
      {
        type: "leaf",
        to: "/configuration/brands",
        labelKey: "nav.brands",
        icon: Tag,
        permissions: ["products:list"],
      },
      {
        type: "leaf",
        to: "/configuration/catalog-import",
        labelKey: "nav.catalogImport",
        icon: Upload,
        permissions: ["products:list"],
      },
    ],
  },
  {
    type: "group",
    key: "fulfillment",
    labelKey: "nav.fulfillment",
    icon: ShoppingCart,
    items: [
      {
        type: "leaf",
        to: "/orders",
        labelKey: "nav.orders",
        icon: ShoppingCart,
        permissions: ["orders:list"],
      },
      {
        type: "leaf",
        to: "/configuration/refunds",
        labelKey: "nav.refunds",
        icon: SlidersHorizontal,
        permissions: ["orders:list"],
      },
    ],
  },
  {
    type: "group",
    key: "customers",
    labelKey: "nav.clients",
    icon: Users,
    items: [
      {
        type: "leaf",
        to: "/clients",
        labelKey: "nav.clients",
        icon: Users,
        permissions: ["clients:list"],
      },
      {
        type: "leaf",
        to: "/bonuses",
        labelKey: "nav.bonuses",
        icon: Gift,
        permissions: ["bonuses:list"],
      },
      {
        type: "leaf",
        to: "/reviews",
        labelKey: "nav.reviews",
        icon: Star,
        permissions: ["reviews:list"],
      },
    ],
  },
  {
    type: "group",
    key: "marketing",
    labelKey: "nav.marketing",
    icon: Megaphone,
    items: [
      {
        type: "leaf",
        to: "/configuration/vouchers",
        labelKey: "nav.vouchers",
        icon: Ticket,
        permissions: ["promo-codes:list"],
      },
      {
        type: "leaf",
        to: "/configuration/promotions",
        labelKey: "nav.promotions",
        icon: Megaphone,
        permissions: ["promo-codes:list"],
      },
      {
        type: "leaf",
        to: "/promo-codes",
        labelKey: "nav.promoCodes",
        icon: Tag,
        permissions: ["promo-codes:list"],
      },
      {
        type: "leaf",
        to: "/banners",
        labelKey: "nav.banners",
        icon: ImageIcon,
        permissions: ["banners:list"],
      },
    ],
  },
  {
    type: "group",
    key: "content",
    labelKey: "nav.content",
    icon: FileText,
    items: [
      {
        type: "leaf",
        to: "/configuration/pages",
        labelKey: "nav.pagesCms",
        icon: FileText,
        permissions: ["settings:view"],
      },
      {
        type: "leaf",
        to: "/site-content",
        labelKey: "nav.siteContent",
        icon: Pencil,
        permissions: ["settings:view"],
      },
    ],
  },
  {
    type: "leaf",
    to: "/analytics",
    labelKey: "nav.analytics",
    icon: BarChart3,
    permissions: ["analytics:view"],
  },
];

// Bottom block — пин снизу sidebar'а как в Saleor (Configuration / Command menu / Playground)
const BOTTOM: Leaf[] = [
  {
    type: "leaf",
    to: "/stores",
    labelKey: "nav.stores",
    icon: Store,
    permissions: ["settings:view"],
  },
  {
    type: "leaf",
    to: "/configuration",
    labelKey: "nav.configuration",
    icon: SlidersHorizontal,
    permissions: ["settings:view"],
  },
  {
    type: "leaf",
    to: "/settings",
    labelKey: "nav.settings",
    icon: Settings,
    permissions: ["settings:view"],
  },
  {
    type: "leaf",
    to: "/users",
    labelKey: "nav.users",
    icon: UserCog,
    permissions: ["admin-users:list"],
  },
];

const STORAGE_KEY = "ri.sidebar.expanded";

function loadExpanded(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

function saveExpanded(v: Record<string, boolean>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(v));
  } catch {
    // ignore
  }
}

function filterVisible<T extends { permissions?: string[] }>(
  items: T[],
  role: UserRole | undefined,
): T[] {
  return items.filter((i) => !i.permissions || canAny(role, i.permissions));
}

export function Sidebar(): JSX.Element {
  const { t } = useTranslation();
  const role = useAuthStore((s) => s.user?.role);
  const { pathname } = useLocation();
  const [expanded, setExpanded] = useState<Record<string, boolean>>(loadExpanded);

  useEffect(() => {
    saveExpanded(expanded);
  }, [expanded]);

  const isActive = (to: string): boolean => {
    if (to === "/") return pathname === "/";
    return pathname === to || pathname.startsWith(to + "/");
  };

  const visibleTop = useMemo(() => {
    return TOP.map((entry): NavEntry | null => {
      if (entry.type === "leaf") {
        if (entry.permissions && !canAny(role, entry.permissions)) return null;
        return entry;
      }
      const items = filterVisible(entry.items, role);
      if (items.length === 0) return null;
      return { ...entry, items };
    }).filter((x): x is NavEntry => x !== null);
  }, [role]);

  const visibleBottom = useMemo(() => filterVisible(BOTTOM, role), [role]);

  const toggle = (key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <aside className="w-64 border-r bg-background flex flex-col h-screen sticky top-0">
      <div className="h-16 flex items-center gap-2 px-4 border-b">
        <img src="/brand/mark-navy.png" alt="" className="w-8 h-8 object-contain" />
        <div className="font-heading text-lg text-primary">Best Digital</div>
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {visibleTop.map((entry) => {
          if (entry.type === "leaf") return <LeafLink key={entry.to} item={entry} t={t} />;

          const childActive = entry.items.some((i) => isActive(i.to));
          const open = expanded[entry.key] ?? childActive;

          return (
            <div key={entry.key}>
              <button
                type="button"
                onClick={() => toggle(entry.key)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                  childActive
                    ? "text-primary"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                )}
                aria-expanded={open}
              >
                <entry.icon className="w-4 h-4 shrink-0" />
                <span className="flex-1 text-left">{t(entry.labelKey)}</span>
                <ChevronDown
                  className={cn(
                    "w-3.5 h-3.5 shrink-0 text-muted-foreground transition-transform",
                    open && "rotate-180",
                  )}
                />
              </button>
              {open && (
                <div className="ml-3 mt-0.5 mb-1 border-l border-border pl-2 space-y-0.5">
                  {entry.items.map((leaf) => (
                    <LeafLink key={leaf.to} item={leaf} t={t} compact />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="border-t py-2 px-2 space-y-0.5">
        {visibleBottom.map((item) => (
          <LeafLink key={item.to} item={item} t={t} />
        ))}
      </div>

      <div className="p-3 border-t text-xs text-muted-foreground">
        v{import.meta.env.VITE_APP_VERSION ?? "2.0.0-dev"}
      </div>
    </aside>
  );
}

function LeafLink({
  item,
  t,
  compact = false,
}: {
  item: Leaf;
  t: (key: string) => string;
  compact?: boolean;
}): JSX.Element {
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-md text-sm font-medium transition-colors",
          compact ? "px-2 py-1.5" : "px-3 py-2",
          isActive
            ? "bg-primary text-primary-foreground"
            : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
        )
      }
    >
      <item.icon className={cn("shrink-0", compact ? "w-3.5 h-3.5" : "w-4 h-4")} />
      <span>{t(item.labelKey)}</span>
    </NavLink>
  );
}
